import React from 'react';
import ReactDOM from 'react-dom';
import { CheckSquare, X } from 'lucide-react';

/**
 * 确认弹窗组件 - 替代浏览器原生 confirm
 * 使用 React Portal 渲染到 document.body，确保始终相对于视口居中
 * @param {string} title - 标题
 * @param {string} message - 提示内容
 * @param {string} confirmText - 确认按钮文字
 * @param {string} cancelText - 取消按钮文字
 * @param {function} onConfirm - 确认回调
 * @param {function} onCancel - 取消回调
 * @param {boolean} danger - 是否为危险操作（按钮变红）
 */
export default function ConfirmModal({
  title = '确认操作',
  message,
  confirmText = '确认',
  cancelText = '取消',
  onConfirm,
  onCancel,
  danger = false
}) {
  if (!message) return null;

  // 使用 Portal 渲染到 body，跳出任何 transform 父元素的影响
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        animation: 'fadeIn 0.2s ease'
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--modal-surface)',
          border: '1px solid rgba(37, 99, 235, 0.4)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
          borderRadius: '16px',
          padding: '24px',
          minWidth: '320px',
          maxWidth: '400px',
          animation: 'popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <CheckSquare size={24} color="#60a5fa" fill="rgba(96, 165, 250, 0.1)" />
          <span style={{
            color: '#93c5fd',
            fontSize: '16px',
            fontWeight: 'bold',
            flex: 1
          }}>
            {title}
          </span>
          <div
            onClick={onCancel}
            style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <X size={20} />
          </div>
        </div>

        {/* 内容 */}
        <div style={{
          color: 'var(--text-body)',
          fontSize: '14px',
          lineHeight: '1.6',
          marginBottom: '24px',
          paddingLeft: '36px'
        }}>
          {message}
        </div>

        {/* 按钮 */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          {cancelText && (
            <button
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid var(--border-strong)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
                e.currentTarget.style.color = 'var(--text-hover)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border-strong)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: danger ? '#dc2626' : '#2563eb',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = danger ? '#b91c1c' : '#1d4ed8';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = danger ? '#dc2626' : '#2563eb';
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes popIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>,
    document.body
  );
}
